/**
 * Auth routes — login/register + onboarding flows.
 * Code-split so public visitors don't ship onboarding forms on first load.
 */
import { lazy, Suspense } from "react";
import { Route } from "react-router-dom";
import {
  AuthRouteGuard,
  ProtectedRoute,
  OnboardingRouteGuard,
} from "../components/common";

// All auth pages lazy
const Login                     = lazy(() => import("../pages/auth").then(m => ({ default: m.Login })));
const Register                  = lazy(() => import("../pages/auth").then(m => ({ default: m.Register })));
const BusinessOnboardingStep    = lazy(() => import("../pages/auth").then(m => ({ default: m.BusinessOnboardingStep })));
const BusinessOnboardingManual  = lazy(() => import("../pages/auth").then(m => ({ default: m.BusinessOnboardingManual })));
const BusinessOnboardingGoogle  = lazy(() => import("../pages/auth").then(m => ({ default: m.BusinessOnboardingGoogle })));
const ClientOnboardingStep     = lazy(() => import("../pages/auth").then(m => ({ default: m.ClientOnboardingStep })));
const BusinessSelector          = lazy(() => import("../pages/auth/BusinessSelector"));
const WelcomeChoice             = lazy(() => import("../pages/auth/WelcomeChoice"));

function ChunkLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

const lazyEl = (Element) => (
  <Suspense fallback={<ChunkLoader />}>
    <Element />
  </Suspense>
);

const AuthRoutes = () => {
  return (
    <>
      <Route path="login"         element={<AuthRouteGuard>{lazyEl(Login)}</AuthRouteGuard>} />
      <Route path="register"      element={<AuthRouteGuard>{lazyEl(Register)}</AuthRouteGuard>} />
      <Route path="auth/login"    element={<AuthRouteGuard>{lazyEl(Login)}</AuthRouteGuard>} />
      <Route path="auth/register" element={<AuthRouteGuard>{lazyEl(Register)}</AuthRouteGuard>} />

      {/* Business selector — multi-business users */}
      <Route path="select-business" element={<ProtectedRoute requireOnboarding={false}>{lazyEl(BusinessSelector)}</ProtectedRoute>} />
      {/* Welcome choice — no business, no client profile */}
      <Route path="welcome" element={<ProtectedRoute requireOnboarding={false}>{lazyEl(WelcomeChoice)}</ProtectedRoute>} />

      {/* Business onboarding — both /auth/onboarding/... and /onboarding/... */}
      <Route path="auth/onboarding/business"        element={<ProtectedRoute requiredRole="business" requireOnboarding={false}><OnboardingRouteGuard>{lazyEl(BusinessOnboardingStep)}</OnboardingRouteGuard></ProtectedRoute>} />
      <Route path="auth/onboarding/business/import" element={<ProtectedRoute requiredRole="business" requireOnboarding={false}><OnboardingRouteGuard>{lazyEl(BusinessOnboardingGoogle)}</OnboardingRouteGuard></ProtectedRoute>} />
      <Route path="auth/onboarding/business/manual" element={<ProtectedRoute requiredRole="business" requireOnboarding={false}><OnboardingRouteGuard>{lazyEl(BusinessOnboardingManual)}</OnboardingRouteGuard></ProtectedRoute>} />
      <Route path="onboarding/business"             element={<ProtectedRoute requiredRole="business" requireOnboarding={false}><OnboardingRouteGuard>{lazyEl(BusinessOnboardingStep)}</OnboardingRouteGuard></ProtectedRoute>} />
      <Route path="onboarding/business/import"      element={<ProtectedRoute requiredRole="business" requireOnboarding={false}><OnboardingRouteGuard>{lazyEl(BusinessOnboardingGoogle)}</OnboardingRouteGuard></ProtectedRoute>} />
      <Route path="onboarding/business/manual"      element={<ProtectedRoute requiredRole="business" requireOnboarding={false}><OnboardingRouteGuard>{lazyEl(BusinessOnboardingManual)}</OnboardingRouteGuard></ProtectedRoute>} />
      <Route
        path="auth/onboarding/client"
        element={
          <ProtectedRoute requiredRole="client" requireOnboarding={false}>
            <OnboardingRouteGuard>{lazyEl(ClientOnboardingStep)}</OnboardingRouteGuard>
          </ProtectedRoute>
        }
      />
    </>
  );
};

export default AuthRoutes;
